import "./style/dinner-suggestions-preferences.scss"

import MenyLocalStorage from "@app/MenyLocalStorage"
import { refreshDinnerSuggestions } from "@app/store/app/appActions"
import { DinnerSuggestionSubject, TrackingEventPage, UserAction } from "@app/types/GtmData"
import Alert from "@ng-mw/reol/components/Alert"
import Button from "@ng-mw/reol/components/Button"
import ButtonGroup from "@ng-mw/reol/components/ButtonGroup"
import Icon from "@ng-mw/reol/components/Icon"
import Spinner from "@ng-mw/reol/components/Spinner"
import checkmarkIcon from "@ng-mw/reol/icons/checkmark"
import { dateHelper, LocalStorage } from "@ng-mw/shared-react-components"
import {
    dinnerSuggestionsImportantToMeOptions,
    dinnerSuggestionsMoreOfOptions,
} from "@modules/recipes/preferences/options"
import {
    fetchDinnerSuggestionsPreferences,
    saveDinnerSuggestionsPreferences,
} from "@modules/recipes/preferences/DinnerSuggestionsPreferenceApi"
import type {
    DinnerSuggestionsImportantToMeOption,
    DinnerSuggestionsImportantToMeOptionId,
    DinnerSuggestionsMoreOfOption,
    DinnerSuggestionsMoreOfOptionId,
} from "@modules/recipes/preferences/types"
import useTrackDinnerSuggestionsEvent from "@modules/recipes/useTrackDinnerSuggestionsEvent"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router"

const dinnerSuggestionsPreferenceErrorMessage = "Kunne ikke lagre preferanser, prøv igjen senere"
const dinnerSuggestionsFetchPreferenceErrorMessage =
    "Kunne ikke hente preferanser, prøv igjen senere"

interface StoredDinnerSuggestionsPreferences {
    isCompleted?: boolean
    completedAt?: string
    preferencesFetchedAt?: string
    moreOf?: DinnerSuggestionsMoreOfOptionId[]
    importantToMe?: DinnerSuggestionsImportantToMeOptionId[]
}

interface PreferenceGroup<TOption> {
    title: string
    items: TOption[]
}

const preferenceGroups: [
    PreferenceGroup<DinnerSuggestionsMoreOfOption>,
    PreferenceGroup<DinnerSuggestionsImportantToMeOption>,
] = [
    {
        title: "Protein",
        items: dinnerSuggestionsMoreOfOptions,
    },
    {
        title: "Egenskaper",
        items: dinnerSuggestionsImportantToMeOptions,
    },
]

const readStoredPreferences = (): StoredDinnerSuggestionsPreferences => {
    return (
        (LocalStorage.get(
            MenyLocalStorage.DinnerSuggestionsOnboarding,
        ) as StoredDinnerSuggestionsPreferences | null) || {}
    )
}

interface DinnerSuggestionsPreferencesProps {
    onClose?: () => void
}

const DinnerSuggestionsPreferences = ({ onClose }: DinnerSuggestionsPreferencesProps) => {
    const history = useHistory()
    const dispatch = useDispatch<any>()
    const trackDinnerSuggestionsEvent = useTrackDinnerSuggestionsEvent()
    const storedPreferences = readStoredPreferences()
    const shouldUseStoredPreferences = storedPreferences.preferencesFetchedAt
        ? dateHelper.isTodayInNorway(storedPreferences.preferencesFetchedAt)
        : false
    const [moreOf, setMoreOf] = useState<DinnerSuggestionsMoreOfOptionId[]>(
        shouldUseStoredPreferences ? storedPreferences.moreOf || [] : [],
    )
    const [importantToMe, setImportantToMe] = useState<DinnerSuggestionsImportantToMeOptionId[]>(
        shouldUseStoredPreferences ? storedPreferences.importantToMe || [] : [],
    )
    const [isLoadingPreferences, setIsLoadingPreferences] = useState(!shouldUseStoredPreferences)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const isBusy = isLoadingPreferences || isSubmitting || !!fetchError
    const selectedPreferencesLookup = useMemo(
        () => new Set<string>([...moreOf, ...importantToMe]),
        [importantToMe, moreOf],
    )

    useEffect(() => {
        if (shouldUseStoredPreferences) return

        let isMounted = true

        const loadPreferences = async () => {
            try {
                const crmPreferences = await fetchDinnerSuggestionsPreferences()

                if (!isMounted) return

                setMoreOf(crmPreferences.moreOf)
                setImportantToMe(crmPreferences.importantToMe)
                persistPreferences(crmPreferences)
            } catch {
                if (isMounted) {
                    setFetchError(dinnerSuggestionsFetchPreferenceErrorMessage)
                }
            } finally {
                if (isMounted) {
                    setIsLoadingPreferences(false)
                }
            }
        }

        void loadPreferences()

        return () => {
            isMounted = false
        }
    }, [shouldUseStoredPreferences])

    const toggleMoreOf = (preferenceId: DinnerSuggestionsMoreOfOptionId) => {
        setMoreOf(currentSelectedPreferences =>
            currentSelectedPreferences.includes(preferenceId)
                ? currentSelectedPreferences.filter(id => id !== preferenceId)
                : [...currentSelectedPreferences, preferenceId],
        )
    }

    const toggleImportantToMe = (preferenceId: DinnerSuggestionsImportantToMeOptionId) => {
        setImportantToMe(currentSelectedPreferences =>
            currentSelectedPreferences.includes(preferenceId)
                ? currentSelectedPreferences.filter(id => id !== preferenceId)
                : [...currentSelectedPreferences, preferenceId],
        )
    }

    const trackPreferenceToggle = (label: string, isChecked: boolean) => {
        trackDinnerSuggestionsEvent({
            action: `${isChecked ? UserAction.TurnOff : UserAction.TurnOn} ${label}`,
            eventPage: TrackingEventPage.DinnerSuggestionPreferences,
        })
    }

    const persistPreferences = ({
        moreOf: nextMoreOf,
        importantToMe: nextImportantToMe,
    }: {
        moreOf: DinnerSuggestionsMoreOfOptionId[]
        importantToMe: DinnerSuggestionsImportantToMeOptionId[]
    }) => {
        LocalStorage.set(MenyLocalStorage.DinnerSuggestionsOnboarding, {
            ...readStoredPreferences(),
            moreOf: nextMoreOf,
            importantToMe: nextImportantToMe,
            preferencesFetchedAt: new Date().toISOString(),
        })
    }

    const savePreferences = async () => {
        if (isBusy) return

        trackDinnerSuggestionsEvent({
            action: `${UserAction.ClickOn} ${DinnerSuggestionSubject.SavePreferences}`,
            eventPage: TrackingEventPage.DinnerSuggestionPreferences,
        })
        setIsSubmitting(true)
        setSubmitError(null)

        try {
            await saveDinnerSuggestionsPreferences({ moreOf, importantToMe })
            persistPreferences({ moreOf, importantToMe })
            dispatch(refreshDinnerSuggestions())
            if (onClose) {
                onClose()
            } else {
                history.goBack()
            }
        } catch {
            setSubmitError(dinnerSuggestionsPreferenceErrorMessage)
            setIsSubmitting(false)
        }
    }

    return (
        <div className="dinner-suggestions-preferences">
            <div className="dinner-suggestions-preferences__content">
                <Alert
                    show
                    type="info"
                    className="dinner-suggestions-preferences__alert"
                    message="Velg hva du vil spise mer av. Du kan fortsatt få andre middagsforslag vi tror passer deg."
                />
                {preferenceGroups.map(group => (
                    <section
                        className="dinner-suggestions-preferences__section"
                        key={group.title}
                    >
                        <h2 className="dinner-suggestions-preferences__section-title">
                            {group.title}
                        </h2>
                        <ul
                            className={
                                group.items.some(item => "description" in item)
                                    ? "dinner-suggestions-preferences__list dinner-suggestions-preferences__list--detailed"
                                    : "dinner-suggestions-preferences__list"
                            }
                        >
                            {isLoadingPreferences ? (
                                <li className="dinner-suggestions-preferences__loading">
                                    <Spinner />
                                </li>
                            ) : (
                                group.items.map(item => {
                                    const isChecked = selectedPreferencesLookup.has(item.id)
                                    const description =
                                        "description" in item ? item.description : undefined

                                    return (
                                        <li
                                            className="dinner-suggestions-preferences__item"
                                            key={item.id}
                                        >
                                            <label className="dinner-suggestions-preferences__label">
                                                <input
                                                    className="dinner-suggestions-preferences__checkbox"
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isBusy}
                                                    onChange={() => {
                                                        trackPreferenceToggle(item.label, isChecked)

                                                        if ("description" in item) {
                                                            toggleImportantToMe(item.id)
                                                        } else {
                                                            toggleMoreOf(item.id)
                                                        }
                                                    }}
                                                />
                                                <span className="dinner-suggestions-preferences__checkbox-visual">
                                                    <Icon
                                                        className="dinner-suggestions-preferences__checkmark"
                                                        icon={checkmarkIcon}
                                                        size={{
                                                            width: "14px",
                                                            height: "14px",
                                                        }}
                                                    />
                                                </span>
                                                <span className="dinner-suggestions-preferences__text">
                                                    <span className="dinner-suggestions-preferences__label-text">
                                                        {item.label}
                                                    </span>
                                                    {description ? (
                                                        <span className="dinner-suggestions-preferences__description">
                                                            {description}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </label>
                                        </li>
                                    )
                                })
                            )}
                        </ul>
                    </section>
                ))}
            </div>
            <div className="dinner-suggestions-preferences__footer">
                {fetchError ? (
                    <Alert
                        show
                        type="error"
                        message={fetchError}
                    />
                ) : null}
                {submitError ? (
                    <Alert
                        show
                        type="error"
                        message={submitError}
                    />
                ) : null}
                <ButtonGroup
                    direction="vertical"
                    alignItems="stretch"
                >
                    <Button
                        text={
                            isLoadingPreferences
                                ? "Laster preferanser"
                                : isSubmitting
                                ? "Lagrer preferanser"
                                : "Lagre preferanser"
                        }
                        onClick={savePreferences}
                        isDisabled={isBusy}
                        isLoading={isBusy}
                    />
                </ButtonGroup>
            </div>
        </div>
    )
}

export default DinnerSuggestionsPreferences
